import React, { useLayoutEffect, useState, useRef } from 'react';
import { Rnd } from 'react-rnd';

import MobileCloseButton from '../MobileCloseButton';
import { SmallCloseSvg, Grabber } from '../Svgs';

import './styles.scss';

export const DraggableContainer = ({
  containerMaxWidth,
  containerMaxHeight,
  containerWidth,
  containerHeight,
  x = 0,
  y = 0,
  onCloseClick,
  children,
}) => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  const objectRef = useRef(null);

  const bringToTop = (container) => {
    const oldOnTopBox = document.getElementById(
      'draggable-box-container-on-top',
    );
    if (oldOnTopBox) {
      oldOnTopBox.classList.remove('--on-top');
      oldOnTopBox.setAttribute('id', '');
    }

    const containerToBringToTop = container || objectRef.current;

    if (containerToBringToTop) {
      containerToBringToTop.classList.add('--on-top');
      containerToBringToTop.setAttribute(
        'id',
        'draggable-box-container-on-top',
      );
    }
  };

  const onContainerMouseDown = (e) => {
    e.preventDefault();
    bringToTop(e.currentTarget);
  };

  useLayoutEffect(() => {
    const onWindowLostFocus = () => {
      if (isMouseOver) {
        bringToTop();
      }
    };

    window.addEventListener('blur', onWindowLostFocus);

    return () => {
      window.removeEventListener('blur', onWindowLostFocus);
    };
  }, [isMouseOver]);

  useLayoutEffect(() => {
    const onWindowLostFocus = () => {
      // Since mobile does not have the blur event, we only loose focus (as far as the proctoring tool is concerned)
      // when we open a new tab and the focus event is emitted (which does exist for mobile).
      // At first I was like Whaa? But if you think about it, it kinda makes sense.
      const embedded = document.getElementById('embedded-pdf-container');
      const current = document.activeElement;
      if (!!embedded && !!current && current.id === embedded.id) {
        setTimeout(() => window.focus(), 5);
      }
    };

    const onDraggableContainerMouseOver = () => {
      if (objectRef) {
        window.focus();
      }
      setIsMouseOver(true);
    };

    const onDraggableContainerMouseOut = () => {
      if (objectRef) {
        window.focus();
      }
      setIsMouseOver(false);
    };

    if (objectRef) {
      objectRef.current.addEventListener(
        'mouseover',
        onDraggableContainerMouseOver,
      );
      objectRef.current.addEventListener(
        'mouseout',
        onDraggableContainerMouseOut,
      );
      window.addEventListener('blur', onWindowLostFocus);
    }

    return () => {
      if (objectRef) {
        objectRef.current.removeEventListener(
          'mouseover',
          onDraggableContainerMouseOver,
        );
        objectRef.current.removeEventListener(
          'mouseout',
          onDraggableContainerMouseOut,
        );
        window.removeEventListener('blur', onWindowLostFocus);
      }
    };
  }, [objectRef]);

  return (
    <div
      ref={objectRef}
      className="draggable-box-container"
      role="presentation"
      onMouseDown={onContainerMouseDown}
    >
      <Rnd
        default={{ x, y, width: containerWidth, height: containerHeight }}
        bounds="window"
        className="draggable-box"
        maxWidth={containerMaxWidth}
        maxHeight={containerMaxHeight}
      >
        <div className="draggable-box-header">
          <div className="grabber-container">
            <Grabber />
          </div>
          <div
            className="close-button-container"
            role="presentation"
            onClick={onCloseClick}
          >
            <SmallCloseSvg />
          </div>
        </div>
        {children}
      </Rnd>
    </div>
  );
};

export const MobileFullscreenContainer = ({ children, onCloseClick }) => (
  <div className="mobile-fullscreen-container" role="presentation">
    <MobileCloseButton increaseClickableArea onClick={onCloseClick} />
    {children}
  </div>
);

export const PdfContainer = ({ url, name, isMobile }) => {
  const iframeLink = `https://media.academicmerit.com/apclassroom/pdfjs/web/viewer.html?file=${url}`;
  const [viewerLoaded, setViewerLoaded] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  const iframeRef = useRef(null);

  // Reset the iframe src property to trigger a reload
  const updateIframe = () => {
    iframeRef.current.src = iframeLink;
  };

  const iframeLoaded = () => {
    setViewerLoaded(true);
  };

  // Sometimes the viewer doesn't load the pdf and the user is left with a blank screen
  // Since it might not error out, just retry reloading after 3 seconds
  useLayoutEffect(() => {
    if (iframeRef.current) {
      if (!viewerLoaded) {
        setIntervalId(setInterval(updateIframe, 10000));
      } else {
        clearInterval(intervalId);
        // hiding toolbars can be done like this if pdfjs is hosted locally
        // iframeRef.current.contentWindow.document.querySelector('.toolbar').style.display = 'none';
        // iframeRef.current.contentWindow.document.querySelector('#sidebarContainer').style.display = 'none';
      }
    }
  }, [viewerLoaded]);

  if (isMobile) {
    // Mobile browsers are unlike to support pdf viewing using the embed tag
    // Leaving this here as a starting point for PREAP-2841
    return (
      <iframe
        onLoad={iframeLoaded}
        onError={updateIframe} // If it errored, just retry immediately
        ref={iframeRef}
        title={`iframe-${name}`}
        className="doc"
        src={iframeLink}
        frameBorder="0"
        border="0"
        height="100%"
        width="100%"
      />
    );
  }

  return (
    <embed
      id="embedded-pdf-container"
      src={url}
      type="application/pdf"
      width="100%"
      height="100%"
    />
  );
};

export const ImageContainer = ({
  alt,
  url,
  maxWidth,
  minWidth,
  maxHeight,
  minHeight,
}) => (
  <img
    onDragStart={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    alt={alt}
    src={url}
    style={{
      maxWidth,
      minWidth,
      maxHeight,
      minHeight,
    }}
  />
);
